import { Component, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BaseUrlConstantsService } from '../../../app/services/base-url-constants.service';
import { BackendApiService } from '../../../app/services/backend-api.service';
import { MatButtonModule } from '@angular/material/button';
interface MajorFeature {
  feature_id: number;
  feature_name: string;
}

interface MinorFeature {
  feature_id: number;
  feature_name: string;
  parent_id: number;
}

interface SubscriptionStatus {
  status: string;
  message: string;
  active: number;
  subscription_flag: string;
  subscription_date: string | null;
  last_subscription_flag: string;
  expiry_date: string | null;
  days_left: number | null;
  needs_subscription: boolean;
  subscription_token: string | null;
}

interface FeaturesResponse {
  status: string;
  major_features: MajorFeature[];
  minor_features: MinorFeature[];
}
@Component({
  selector: 'app-my-subscription',
  standalone: true,
  imports: [MatIconModule, CommonModule, FormsModule, MatButtonModule],
  templateUrl: './my-subscription.component.html',
  styleUrl: './my-subscription.component.scss'
})
export class MySubscriptionComponent implements OnInit {
  constructor(private service: BackendApiService) {}
  private clientId: string = localStorage.getItem('user_id');
  private SUBSCRIPTION_STATUS_URL = BaseUrlConstantsService.CLIENTS_SUBSCRIPTIONS_URL;
  public loading: boolean = true;
  public subscriptionStatus: SubscriptionStatus | null = null;
  public features: FeaturesResponse | null = null;
  public groupedFeatures: { [key: number]: { major: MajorFeature, minors: MinorFeature[] } } = {};
  public ON_PREM: boolean = BaseUrlConstantsService.ON_PREM;
  public showRequestTokenModal: boolean = false;
  public modalTitle: string = 'Request Subscription Token';
  public modalMessage: string = 'Please contact your account manager to request a subscription token.';
  public sendingRequestToken: boolean = false;
  public isMailReceived: boolean = false;
  public activationToken: string = '';
  public isVerifying: boolean = false;
  public verifcationStatus: {
    type: 'success' | 'error' | 'loading';
    message: string;
  } | null = null;

  ngOnInit(): void {
    this.loadSubscriptionData();
    console.log("On Prem Status:", this.ON_PREM);
  }

  async loadSubscriptionData(): Promise<void> {
    try {
      this.loading = true;
      const [featuresResponse, statusResponse, mailSentResponse] = await Promise.all([
        this.loadFeatures(),
        this.loadSubscriptionStatus(),
        this.loadMailSent()
      ]);
      this.isMailReceived = mailSentResponse?.status === 'success' && mailSentResponse?.mail_received === 1;
      this.features = featuresResponse;
      this.subscriptionStatus = statusResponse;
      console.log('Subscription Status:', this.subscriptionStatus);

      if (this.features) {
        this.groupFeatures();
      }

    } catch (error) {
      console.error('Error loading subscription data:', error);
    } finally {
      this.loading = false;
    }
  }

  async loadFeatures() {
    try {
      const response: any = await this.service.getDataV2(BaseUrlConstantsService.GET_MY_SUBSCRIPTION_DATA + `/${this.clientId}`);
      if(response.status === 'success') {
        return response;
      } else {
        console.log('Failed to fetch features: status not success');
      }
    }
    catch (error) {
      console.error('Error fetching features:', error);
      throw error;
    }
  }

  async loadSubscriptionStatus() {
    try {
      const response: any = await this.service.postData(this.SUBSCRIPTION_STATUS_URL, { clientId: this.clientId });
      if(response.status === 'success') {
        return response;
      } else {
        console.log('Failed to fetch features: status not success');
      }
    }
    catch (error) {
      console.error('Error fetching features:', error);
      throw error;
    }
  }

  async loadMailSent(): Promise<any> {
    try {
      const response: any = await this.service.postData(BaseUrlConstantsService.GET_MAIL_SENT, { clientId: this.clientId });
      if(response.status === 'success') {
        return response;
      } else {
        console.log('Failed to fetch mail sent status: status not success');
      }
    }
    catch (error) {
      console.error('Error fetching mail sent status:', error);
      throw error;
    }
  }


  groupFeatures(): void {
    if(!this.features) return;
    this.groupedFeatures = {};
    this.features.major_features.forEach(major => {
      const minors = this.features!.minor_features.filter(minor => minor.parent_id === major.feature_id);
      this.groupedFeatures[major.feature_id] = {
        major,
        minors
      };
    })
    console.log('Grouped Features:', this.groupedFeatures);
  }

  getStatusColor(): string {
    if (!this.subscriptionStatus) return 'text-gray-400';

    switch (this.subscriptionStatus.subscription_flag) {
      case 'ACTIVE': return 'text-emerald-400';
      case 'TRIAL': return 'text-blue-400';
      case 'EXPIRED': return 'text-red-400';
      case 'PENDING RENEW': return 'text-amber-400';
      case 'RENEWED': return 'text-emerald-400';
      default: return 'text-gray-400';
    }
  }

  getStatusIcon(): string {
    if (!this.subscriptionStatus) return 'help_outline';

    switch (this.subscriptionStatus.subscription_flag) {
      case 'ACTIVE': return 'check_circle';
      case 'TRIAL': return 'schedule';
      case 'EXPIRED': return 'error';
      case 'PENDING RENEW': return 'refresh';
      case 'RENEWED': return 'check_circle';
      default: return 'help_outline';
    }
  }

  getDaysLeftColor(): string {
    if (!this.subscriptionStatus?.days_left) return 'text-gray-400';

    const daysLeft = this.subscriptionStatus.days_left;
    if (daysLeft <= 0) return 'text-red-400';
    if (daysLeft <= 7) return 'text-amber-400';
    if (daysLeft <= 15) return 'text-blue-400';
    return 'text-emerald-400';
  }

  renewSubscription(): void {
    // Implement your subscription renewal logic here
    console.log('Renewing subscription...');
  }

  upgradeSubscription(): void {
    if(!this.ON_PREM) {
      if(this.isMailReceived) {
        this.modalMessage = "You have already requested a token. Please check your email for the token. If you haven't received it, please contact your administrator.";
      }
      else {
        this.modalMessage = "Please click on Request Token button to send mail to admin for subscription token.";
      }
    }
    else {
      this.modalMessage = "Please contact your administrator or email development@enertics.ca to enable or add a token for your account.";
    }
    this.showRequestTokenModal = true;
  }

  async requestTokenFromAdmin() {
    if (this.sendingRequestToken) return;
    this.sendingRequestToken = true;
    console.log('Requesting token...');
    try {
      const response: any = await this.service.postData(BaseUrlConstantsService.REQUEST_TOKEN_URL, { clientId: this.clientId });
      console.log('Response from request token:', response);
      if(response.status === 'success') {
        this.modalMessage = response.message || "Token request successful. Please check your email for the token.";
      }
      else {
        this.modalMessage = response.message || "Failed to request token. Please try again later.";
      }
    }
    catch (error) {
      console.error('Error requesting token:', error);
    }
    finally {
      this.sendingRequestToken = false;
    }
  }

  closeRequestTokenModal() {
    this.showRequestTokenModal = false;

  }

  async verifyToken() {
    console.log('Verifying token:', this.activationToken);
    // if(this.activationToken.trim()) {
    //   this.showVerificationStatus('error', 'Please enter an activation token');
    //   return;
    // }

    // if(!this.isValidTokenFormat(this.activationToken)) {
    //   this.showVerificationStatus('error', 'Invalid token format. Please enter a valid token.');
    //   return;
    // }

    this.isVerifying = true;
    this.showVerificationStatus('loading', 'Verifying your activation token...');

    try {
      const response: any = await this.service.postData(BaseUrlConstantsService.VERIFY_TOKEN_URL, { client_id: this.clientId, subscriptionKey: this.activationToken });
      console.log('Response from verify token:', response);
      if(response.status === 'success') {
        this.showVerificationStatus('success', response.message || "Token verified successfully! Your subscription has been activated.");
        this.loadSubscriptionStatus();
        this.loadSubscriptionData();
        this.clearTokenForm();

      }
      else {
        this.showVerificationStatus('error', response.message || "Failed to verify token. Please try again later.");
      }
    }
    catch(error) {
      console.error('Token verification error:', error);

      let errorMessage = 'An error occurred while verifying the token. Please try again.';

      if (error.status === 400) {
        errorMessage = 'Invalid token. Please check your token and try again.';
      } else if (error.status === 404) {
        errorMessage = 'Token not found or has expired.';
      } else if (error.status === 409) {
        errorMessage = 'This token has already been used.';
      } else if (error.error?.message) {
        errorMessage = error.error.message;
      }

      this.showVerificationStatus('error', errorMessage);
    }
    finally {
      this.isVerifying = false;
      // Reset the activation token field
      this.activationToken = '';
      // Clear the verification status after a delay
      setTimeout(() => {
        if(this.verifcationStatus?.type === 'success') {
          this.verifcationStatus = null;
        }
      }, 2000);
    }
  }

  private showVerificationStatus(type: 'success' | 'error' | 'loading', message: string) {
    this.verifcationStatus = { type, message };
    if(type === "success") {
      setTimeout(() => {
        setTimeout(() => {
          if(this.verifcationStatus?.type === 'success') {
            this.verifcationStatus = null;
          }
        }, 5000)
      })
    }
  }

  private isValidTokenFormat(token: string): boolean {
    const cleanToken = token.replace(/\s+/g, '').toUpperCase();

    // Check for common token formats:
    // 1. XXX-XXX-XXX format (with hyphens)
    const hyphenFormat = /^[A-Z0-9]{3,}-[A-Z0-9]{3,}-[A-Z0-9]{3,}$/;

    // 2. Continuous alphanumeric format (12+ characters)
    const continuousFormat = /^[A-Z0-9]{12,}$/;

    // 3. UUID format (with or without hyphens)
    const uuidFormat = /^[A-Z0-9]{8}-?[A-Z0-9]{4}-?[A-Z0-9]{4}-?[A-Z0-9]{4}-?[A-Z0-9]{12}$/;

    return hyphenFormat.test(cleanToken) ||
           continuousFormat.test(cleanToken) ||
           uuidFormat.test(cleanToken);
  }

  private clearTokenForm() {
    this.activationToken = '';
    this.verifcationStatus = null;
  }

}
